import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { Button } from '@/shared/ui/Button';
import { PageHeader, Stat } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import {
  formatConversions,
  formatCurrency,
  formatNumber,
  formatPercent,
} from '@/shared/utils/format';
import { DayHourHeatmap } from '../components/DayHourHeatmap';
import { AdsConnectionFailed } from '../components/AdsConnectionFailed';
import { useAdsState } from '../hooks/useAdsState';
import {
  usePerformanceSummary,
  useChannelBreakdown,
  useCampaignBreakdown,
  useTopSearchTerms,
  useHeatmap,
} from '../hooks/usePerformance';
import type { PerformanceRange, PerformanceRangeParams, CampaignRow } from '../api/campaignsApi';
import styles from './PerformancePage.module.css';

const CHANNEL_COLORS: Record<string, string> = {
  google: 'var(--google)',
  meta: 'var(--meta)',
};

const CHANNEL_LABELS: Record<string, string> = {
  google: 'Google Ads',
  meta: 'Meta',
};

const RANGE_OPTIONS: Array<{ value: PerformanceRange; label: string }> = [
  { value: '15d', label: '15d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1 year' },
  { value: 'custom', label: 'Custom' },
];

const RANGE_SUMMARY_LABELS: Record<PerformanceRange, string> = {
  '15d': 'Last 15 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  ytd: 'Year to date',
  '1y': 'Last 12 months',
  custom: 'Custom range',
};

export function PerformancePage() {
  const navigate = useNavigate();

  // Check platform connection status
  const adsState = useAdsState();
  const isGoogleConnected = adsState.accounts.google;
  const statusLoading = !adsState.accountsLoaded;
  const anyConnected = adsState.accounts.any;

  // ─── Date range ────────────────────────────────────────────────────────────
  // Kept in the URL (?range=&from=&to=) so a campaign opened from here can send
  // the user back to this exact view — same range — via the detail page's Back link.
  const [searchParams, setSearchParams] = useSearchParams();
  const rangeParam = searchParams.get('range');
  const range: PerformanceRange = RANGE_OPTIONS.find((o) => o.value === rangeParam)?.value ?? '30d';
  const customFrom = searchParams.get('from') ?? '';
  const customTo = searchParams.get('to') ?? '';
  const updateParams = (mut: (p: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mut(next);
        return next;
      },
      { replace: true },
    );
  };
  const setRange = (r: PerformanceRange) =>
    updateParams((p) => {
      p.set('range', r);
      if (r !== 'custom') {
        p.delete('from');
        p.delete('to');
      }
    });
  const setCustomFrom = (v: string) =>
    updateParams((p) => (v ? p.set('from', v) : p.delete('from')));
  const setCustomTo = (v: string) => updateParams((p) => (v ? p.set('to', v) : p.delete('to')));
  const rangeParams: PerformanceRangeParams = { range, from: customFrom, to: customTo };
  const customIncomplete = range === 'custom' && !(customFrom && customTo);

  // Back-navigation target for campaign links opened from this page.
  const location = useLocation();
  const backState = { from: location.pathname + location.search, fromLabel: 'Performance' };

  // Performance data queries
  const summaryQuery = usePerformanceSummary(rangeParams);
  const channelsQuery = useChannelBreakdown(rangeParams);
  const campaignsQuery = useCampaignBreakdown(rangeParams);
  const searchTermsQuery = useTopSearchTerms(rangeParams);
  const heatmapQuery = useHeatmap(rangeParams);

  // Range dropdown lives on the right of the page header, consistent with the
  // Ads Overview page. Custom date inputs drop below the header when selected.
  const rangeSelect = (
    <select
      className={styles.rangeSelect}
      value={range}
      onChange={(e) => setRange(e.target.value as PerformanceRange)}
      aria-label="Date range"
    >
      {RANGE_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  const customDateRow = range === 'custom' && (
    <div className={styles.customRange}>
      <input
        type="date"
        className={styles.dateInput}
        value={customFrom}
        max={customTo || undefined}
        onChange={(e) => setCustomFrom(e.target.value)}
        aria-label="Start date"
      />
      <span className={styles.dateSep}>to</span>
      <input
        type="date"
        className={styles.dateInput}
        value={customTo}
        min={customFrom || undefined}
        onChange={(e) => setCustomTo(e.target.value)}
        aria-label="End date"
      />
    </div>
  );

  const summary = summaryQuery.data?.data;
  const channels = channelsQuery.data?.data ?? [];
  const campaigns = campaignsQuery.data?.data ?? [];
  const searchTerms = searchTermsQuery.data?.data ?? [];
  const heatmap = heatmapQuery.data?.data;
  const [visibleTerms, setVisibleTerms] = useState(25);

  // Just-launched active campaigns (no stats yet) sit at the top, then active
  // campaigns with data, then paused ones (grouped + de-emphasized).
  const bySpend = (a: CampaignRow, b: CampaignRow) => b.spend - a.spend;
  const justLaunchedRows = campaigns.filter((c) => c.awaitingData);
  const activeCampaignRows = campaigns
    .filter((c) => !c.awaitingData && c.status !== 'paused')
    .sort(bySpend);
  const pausedCampaignRows = campaigns
    .filter((c) => !c.awaitingData && c.status === 'paused')
    .sort(bySpend);

  const renderCampaignRow = (c: CampaignRow, paused: boolean) => (
    <div
      key={c.id}
      className={`${styles.gridTableRow} ${styles.gridCols7wide} ${paused ? styles.rowPaused : ''}`}
    >
      <div className={styles.channelCell}>
        <span
          className={styles.channelDot}
          style={{ background: CHANNEL_COLORS[c.platform] ?? 'var(--gray-500)' }}
          title={CHANNEL_LABELS[c.platform] ?? c.platform}
        />
        {c.marketingCampaignId ? (
          <Link
            to={`/campaigns/${c.marketingCampaignId}`}
            state={backState}
            className={styles.cellNameLink}
          >
            {c.name}
          </Link>
        ) : (
          <span className={styles.cellName}>{c.name}</span>
        )}
        {c.awaitingData && <span className={styles.justLaunchedTag}>Just launched</span>}
      </div>
      {c.awaitingData ? (
        <span className={styles.awaitingNote} style={{ gridColumn: '2 / -1' }}>
          Live — collecting data. Stats appear here once the platform reports them.
        </span>
      ) : (
        <>
          <span className={styles.cellPrimary}>{formatCurrency(c.spend)}</span>
          <span className={styles.cellSecondary}>{formatNumber(c.impressions)}</span>
          <span className={styles.cellSecondary}>{formatNumber(c.clicks)}</span>
          <span className={styles.cellSecondary}>{formatPercent(c.ctr * 100)}</span>
          <span className={c.conversions > 0 ? styles.cellPrimary : styles.cellMuted}>
            {formatConversions(c.conversions)}
          </span>
          <span className={styles.cellSecondary}>
            {c.conversions > 0 ? formatCurrency(c.cpa) : '—'}
          </span>
        </>
      )}
    </div>
  );

  const isLoading = statusLoading || summaryQuery.isLoading;

  // ─── Zero state: locked ──────────────────────────────────────────────────────
  if (adsState.accounts.connectionFailed) {
    return (
      <AdsConnectionFailed
        title="Performance"
        subtitle="All metrics as reported by ad platforms"
        className={styles.page}
        failures={adsState.accounts.failed}
      />
    );
  }

  if (!statusLoading && !anyConnected) {
    return (
      <div className={styles.page}>
        <PageHeader title="Performance" subtitle="All metrics as reported by ad platforms" />

        <EmptyState
          title="Connect an Ad Platform"
          description="Meta Ads is managed by your agency. Connect Google Ads to see campaign performance — spend breakdown, conversions, top search terms, and day/hour heatmaps all in one place."
          actions={
            <Button variant="primary" onClick={() => navigate('/settings/paid')}>
              Connect Google Ads
            </Button>
          }
        />
      </div>
    );
  }

  // ─── Loading state ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="Performance"
          subtitle="All metrics as reported by ad platforms"
          actions={rangeSelect}
        />
        {customDateRow}
        <div className={styles.summaryGrid}>
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  // ─── Helper: build channel breakdown rows for a summary card ─────────────────
  function renderChannelBreakdown(
    byChannel: Record<string, number> | undefined,
    formatter: (v: number) => string,
  ) {
    if (!byChannel) return null;
    const entries = Object.entries(byChannel).filter(([, v]) => v > 0);
    if (entries.length === 0) return null;
    return (
      <div className={styles.channelBreakdown}>
        {entries.map(([key, val]) => (
          <div key={key} className={styles.channelBreakdownRow}>
            <span className={styles.channelBreakdownName}>
              <span
                className={styles.channelBreakdownDot}
                style={{ background: CHANNEL_COLORS[key] ?? 'var(--gray-500)' }}
              />
              {CHANNEL_LABELS[key] ?? key}
            </span>
            <span className={styles.channelBreakdownValue}>{formatter(val)}</span>
          </div>
        ))}
      </div>
    );
  }

  // ─── Active / empty state ────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <PageHeader
        title="Performance"
        subtitle="All metrics as reported by ad platforms"
        actions={rangeSelect}
      />

      {customDateRow}

      {customIncomplete && (
        <div className={styles.rangeHint}>Pick a start and end date to see performance.</div>
      )}

      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <Stat
            size="lg"
            label="Total Spend"
            value={formatCurrency(summary?.totalSpend ?? 0)}
            tone={summary?.totalSpend ? 'default' : 'muted'}
            sub={
              renderChannelBreakdown(summary?.spendByChannel, formatCurrency) ??
              RANGE_SUMMARY_LABELS[range]
            }
          />
        </div>
        <div className={styles.summaryCard}>
          <Stat
            size="lg"
            label="Impressions"
            value={formatNumber(summary?.impressions ?? 0)}
            tone={summary?.impressions ? 'default' : 'muted'}
            sub={renderChannelBreakdown(summary?.impressionsByChannel, formatNumber)}
          />
        </div>
        <div className={styles.summaryCard}>
          <Stat
            size="lg"
            label="Clicks"
            value={formatNumber(summary?.clicks ?? 0)}
            tone={summary?.clicks ? 'default' : 'muted'}
            sub={`${formatPercent(summary?.ctr ? summary.ctr * 100 : 0)} CTR`}
          />
        </div>
        <div className={styles.summaryCard}>
          <Stat
            size="lg"
            label="Conversions"
            value={formatConversions(summary?.conversions)}
            tone={summary?.conversions ? 'default' : 'muted'}
            sub={`${formatCurrency(summary?.cpa ?? 0)} CPA`}
          />
        </div>
      </div>

      {/* Channel Performance */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Channel Performance</div>
        <div className={styles.gridTable}>
          <div className={`${styles.gridTableHeader} ${styles.gridCols7}`}>
            <span>Channel</span>
            <span>Spend</span>
            <span>Impr.</span>
            <span>Clicks</span>
            <span>CTR</span>
            <span>Conv.</span>
            <span>CPA</span>
          </div>
          {channels.length === 0 ? (
            <div className={styles.emptyRow}>No channel data yet</div>
          ) : (
            channels.map((ch) => (
              <div key={ch.channel} className={`${styles.gridTableRow} ${styles.gridCols7}`}>
                <div className={styles.channelCell}>
                  <span
                    className={styles.channelDot}
                    style={{ background: CHANNEL_COLORS[ch.channel] ?? 'var(--gray-500)' }}
                  />
                  <span className={styles.cellPrimary}>
                    {CHANNEL_LABELS[ch.channel] ?? ch.channel}
                  </span>
                </div>
                <span className={styles.cellPrimary}>{formatCurrency(ch.spend)}</span>
                <span className={styles.cellSecondary}>{formatNumber(ch.impressions)}</span>
                <span className={styles.cellSecondary}>{formatNumber(ch.clicks)}</span>
                <span className={styles.cellSecondary}>{formatPercent(ch.ctr * 100)}</span>
                <span className={styles.cellPrimary}>{formatConversions(ch.conversions)}</span>
                <span className={styles.cellSecondary}>{formatCurrency(ch.cpa)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Campaign Breakdown */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Campaign Breakdown</div>
        <div className={styles.gridTable}>
          <div className={`${styles.gridTableHeader} ${styles.gridCols7wide}`}>
            <span>Campaign</span>
            <span>Spend</span>
            <span>Impr.</span>
            <span>Clicks</span>
            <span>CTR</span>
            <span>Conv.</span>
            <span>CPA</span>
          </div>
          {campaigns.length === 0 ? (
            <div className={styles.emptyRow}>No campaigns yet</div>
          ) : (
            <>
              {justLaunchedRows.map((c) => renderCampaignRow(c, false))}
              {activeCampaignRows.map((c) => renderCampaignRow(c, false))}
              {pausedCampaignRows.length > 0 && (
                <div className={styles.pausedGroupLabel}>Paused · {pausedCampaignRows.length}</div>
              )}
              {pausedCampaignRows.map((c) => renderCampaignRow(c, true))}
            </>
          )}
        </div>
      </div>

      {/* Top Search Terms */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Top Search Terms</div>
        <div className={styles.gridTable}>
          <div className={`${styles.gridTableHeader} ${styles.gridCols5}`}>
            <span>Search Term</span>
            <span>Impr.</span>
            <span>Clicks</span>
            <span>Conv.</span>
            <span>Cost</span>
          </div>
          {searchTerms.length === 0 ? (
            <div className={styles.emptyRow}>
              {isGoogleConnected
                ? 'No search term data yet'
                : 'Connect Google Ads to see search terms'}
            </div>
          ) : (
            searchTerms.slice(0, visibleTerms).map((t) => (
              <div key={t.term} className={`${styles.gridTableRow} ${styles.gridCols5}`}>
                <span className={styles.cellName}>{t.term}</span>
                <span className={styles.cellSm}>{formatNumber(t.impressions)}</span>
                <span className={styles.cellSm}>{formatNumber(t.clicks)}</span>
                <span className={t.conversions > 0 ? styles.cellSmBold : styles.cellSmMuted}>
                  {formatConversions(t.conversions)}
                </span>
                <span className={styles.cellSm}>{formatCurrency(t.cost)}</span>
              </div>
            ))
          )}
        </div>
        {visibleTerms < searchTerms.length && (
          <div className={styles.loadMore}>
            <Button variant="secondary" onClick={() => setVisibleTerms((v) => v + 25)}>
              Load more
            </Button>
          </div>
        )}
      </div>

      {/* Day & Hour Heatmap */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Performance by Day & Hour</div>
        <DayHourHeatmap cells={heatmap?.cells ?? []} aiInsight={heatmap?.aiInsight} />
      </div>
    </div>
  );
}
